/**
 * Fonts Container
 */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push, replace } from 'react-router-redux';
import styles from './styles.css';
import { generateLicensePhrase, licenseIsPopulated, shouldHideSpecialOffer } from 'utils';
import autobind from 'class-autobind';
import Helmet from 'react-helmet';
import classNames from 'classnames';


// Lodash
import { find, get, isEmpty, isEqual, last } from 'lodash';


// Config / Settings
import { app as AppLang } from 'language';
import { sources } from '../../../internals/config';


// Components
import PageHeader from 'components/PageHeader';
import FontsFooter from 'components/FontsFooter';


// Selectors
import { selectUserProfile } from 'store/account/selectors';
import { createStructuredSelector } from 'reselect';
import {
	selectEditorControlsOpen,
	selectProductLine,
	selectProducts,
	selectProductStyles,
	selectSpecialOffers
} from 'store/fonts/selectors';
import { selectBreakpoints, selectCart, selectMobileState } from '../App/selectors';
import { selectLicenseState } from 'store/licenses/selectors';


// Actions
import {
	clearProductLine,
	loadProducts,
	loadProductStyles,
	loadSpecialOffers,
	setProductLine,
	loadSuggestedPairings
} from 'store/fonts/actions';
import {
	setBreadcrumbState,
	setFooter,
	setMenu,
	setSiteHeaderState,
	clearSiteHeaderState
} from '../App/actions';

export class Fonts extends React.Component {
	constructor(props) {
		super(props);
		autobind(this);
	}

	componentDidMount() {
		const {
			onLoadProducts,
			onLoadSpecialOffers,
			params,
			productLine,
			products
		} = this.props;

		// Load all products & offers if they haven't been loaded yet
		if (!products) {
			onLoadProducts();
			onLoadSpecialOffers();
		}

		/**
		 * Load a productLine if the products have been loaded and the font
		 * param has been set in the URL
		 */
		if (products && !productLine && params.font) {
			this.handleSetProductLine();
		}
	}

	componentDidUpdate(prevProps) {
		const { onLoadSuggestedPairings, params, products, productLine } = this.props;

		if (prevProps.productLine !== productLine && productLine) {
			this.props.onLoadProductStyles();
		}

		// Set product line conditionally
		if (
			products && !!params.font &&
			(productLine === null || prevProps.productLine !== null)
		) {
			this.handleSetProductLine(prevProps.productLine);
		}

		if (
			!prevProps.productLine && productLine
			|| prevProps.productLine && prevProps.productLine.id !== productLine.id
		) {
			onLoadSuggestedPairings();
		}
	}

	componentWillUnmount() {
		const { onClearProductLine, clearSiteHeader, setBreadcrumb } = this.props;
		setBreadcrumb({
			current: {
				shortLabel: ''
			},
			ctaLink: null
		});
		// Clear out the product line, packages and prices when we exit
		onClearProductLine();
		clearSiteHeader();
	}

	/**
	 * Set the state of the SiteHeader on collapse of the PageHeader.
	 * @param {Boolean} isPageHeaderCollapsed collapsed state of PageHeader
	 */
	onPageHeaderCollapse(isPageHeaderCollapsed) {
		const { isMobile, setSiteHeader } = this.props;
		if (!isMobile) {
			setSiteHeader({ isDimmed: isPageHeaderCollapsed });
		}
	}

	/**
	 * Set the BreadcrumbBar state for the current route
	 * (Some child pages set their own BreadcrumbBar state,
	 * but most delegate to the Font container)
	 * @param {Boolean} props Props from which the BreadcrumbBar state should be set
	 */
	setBreadcrumbBar(props) {
		const {
			setBreadcrumb,
			onSetMenu,
			productLine,
			children,
			params,
			route
		} = props;

		if (!children) {
			setBreadcrumb({
				parent: { label: 'H&Co', path: '/' },
				current: { label: route.label, path: route.path },
				showSecondary: false
			});
		} else if (params.font) {
			setBreadcrumb({
				onclick: () => { onSetMenu('fonts'); },
				parent: { label: route.label },
				current: {
					label: get(productLine, 'attributes.name'),
					shortLabel: get(productLine, 'attributes.short_name'),
					path: `/fonts/${get(productLine, 'attributes.slug')}`,
					typeCapsule: get(productLine, 'attributes.type_capsule')
				},
				showSecondary: true,
				ctaLink: `/fonts/${get(productLine, 'attributes.slug')}/styles`
			});
		}
	}

	UNSAFE_componentWillMount() { // eslint-disable-line camelcase
		// Redirect from '/fonts' to '/fonts/latest-releases'
		if (this.props.location.pathname === '/fonts' && !this.props.params.font) {
			this.props.replaceRoute('/fonts/latest-releases');
		}

		const { onSetFooter } = this.props;
		this.setBreadcrumbBar(this.props);

		// Show/hide footer based on option in routes
		const currentRoute = last(this.props.routes);
		if (currentRoute.footerOption) { onSetFooter({ type: currentRoute.footerOption }); }

		this.redirectToOverview();
	}

	UNSAFE_componentWillReceiveProps() { // eslint-disable-line camelcase
		this.redirectToOverview();
	}

	UNSAFE_componentWillUpdate(nextProps) { // eslint-disable-line camelcase
		const { location, onLoadSpecialOffers, productLine } = this.props;
		const hasChangedLocation = location.pathname !== nextProps.location.pathname;

		if (!isEqual(productLine, nextProps.productLine) || hasChangedLocation) {
			this.setBreadcrumbBar(nextProps);
		}

		if (productLine === null && nextProps.productLine !== null) {
			onLoadSpecialOffers();
		}
	}

	/**
	 * Set the product line based on the existence of a font param in the route.
	 *
	 * @method handleSetProductLine
	 * @return {Null}
	 */
	handleSetProductLine(prevProductLine) {
		const {
			params,
			products,
			onSetProductLine,
			onLoadSpecialOffers
		} = this.props;

		const font = params.font;
		const theProductLine = find(products, product => product.attributes.slug === font);

		// Forward to 404 page if non-existent product line is passed in URL
		if (!theProductLine) {
			this.props.replaceRoute('/404');
			return false;
		}

		if (!isEqual(prevProductLine, theProductLine)) {
			onLoadSpecialOffers();
			onSetProductLine(theProductLine);
		}

		return true;
	}

	/**
	 * If the user is hitting the '/fonts/:font' path without a subdirectory, forward them to
	 * the Overview page. We used to handle this in the routes file with onEnter, but it was
	 * mysteriously being redirected twice.
	 *
	 * @method  redirectToOverview
	 * @return  {Void}
	 */
	redirectToOverview() {
		const currentRoute = last(this.props.routes);
		if (currentRoute.name === 'font') {
			this.props.replaceRoute({ pathname: `${this.props.location.pathname}/overview` });
		}
	}

	renderFooter() {
		const {
			breakpoints,
			cart,
			changeRoute,
			editorControlsOpen,
			licenses,
			params,
			productLine,
			routes,
			specialOffers,
			user
		} = this.props;

		const childRoute = last(routes);
		let link;
		let footerSpecialOffers = specialOffers;

		// Hide the special offer if a self-hosted license is the only selected license
		if (shouldHideSpecialOffer(licenses)) { footerSpecialOffers = null; }

		// Hide the footer on these pages
		if (params.font && ['styles', 'package'].indexOf(childRoute.name) < 0) {
			link = { to: `/fonts/${params.font}/styles`, text: 'Get these fonts', isColorSecondary: true };
		} else if (cart && !isEmpty(cart.items)) {
			footerSpecialOffers = null;
			link = { to: '/cart', text: 'Proceed to Checkout', isColorSecondary: true };
		}

		return (
			<FontsFooter
				isMobileFixedToScreen
				changeRoute={ changeRoute }
				containerClassName={
					classNames(
						styles.fontsFooterContainer, {
							[styles['fontsFooterContainer--controlsOpen']]: breakpoints.gtLargeMedium && editorControlsOpen
						}
					)
				}
				licenses={ licenses }
				link={ link }
				productLine={ productLine }
				specialOffers={ footerSpecialOffers }
				user={ user }
			/>
		);
	}

	/**
	 * Generate the Helmet code for inclusion in the header
	 * @method  renderHelmet
	 * @return  {Node}
	 */
	renderHelmet() {
		const { productLine } = this.props;
		if (isEmpty(productLine)) { return false; }

		const family = get(productLine, 'attributes.name');
		const childRoute = last(this.props.routes);
		const title = `${family} - ${childRoute.label} | ${AppLang.pageTitle}`;
		const summary = get(productLine, 'attributes.top_hit_description');

		const ogImage = get(productLine, 'attributes.open_graph_image', null);
		const ogImagePath = ogImage ? `${sources.cloudfrontURL}/openGraphImages/${ogImage}` : null;

		const topHitImage = get(productLine, 'attributes.top_hit_image');
		const thImagePath = topHitImage ? `${sources.cloudfrontURL}/topHitImages/${topHitImage}` : null;

		const whichImage = !!ogImage ? ogImagePath : thImagePath; // eslint-disable-line no-extra-boolean-cast

		return (
			<Helmet>
				<title>{ family }</title>

				<meta content={ `${family} — ${summary}` } name="description" />

				{/* Twitter */}
				<meta content={ title } name="twitter:title" />
				<meta content={ `${family} - ${summary}` } name="twitter:description" />
				<meta content={ whichImage } name="twitter:image" />

				{/* Facebook, etc. */}
				<meta content={ location } property="og:url" />
				<meta content="article" property="og:type" />
				<meta content={ title } property="og:title" />
				<meta content={ `${family} — ${summary}` } property="og:description" />
				<meta content={ whichImage } property="og:image" />
				<meta content="1200" property="og:image:width" />
				<meta content="960" property="og:image:height" />
			</Helmet>
		);
	}

	/**
	 * Find the index of an object in an array based on the
	 * value of one of its properties
	 * @method  findWithAttr
	 * @return  int
	 */
	findWithAttr(array, attr, value) {
		for (let i = 0; i < array.length; i += 1) {
			if (array[i][attr] === value) {
				return i;
			}
		}
		return -1;
	}


	/**
	 * Render the page header conditionally
	 * @method  renderPageHeader
	 * @return  {Node}
	 */
	renderPageHeader() {
		const {
			breakpoints,
			changeRoute,
			licenses,
			location,
			params,
			productLine,
			route,
			specialOffers,
			user
		} = this.props;
		const childRoute = last(this.props.routes);

		const message = generateLicensePhrase({
			computerCount: licenses.computer.value,
			computers: licenses.computer.value,
			web: licenses.cloud.inCart || get(user, 'subscriptions.cloud', false),
			apps: licenses.app.inCart || get(user, 'subscriptions.app', false)
		});

		const specialOfferText = get(specialOffers, 'special_offers.special_offers.0.text');
		const specialOfferLinks = get(specialOffers, 'special_offers.special_offers.0.links', []);

		// Hide the special offer if a self-hosted license is the only selected license
		const hideSpecialOffer = shouldHideSpecialOffer(licenses);

		const pageNav = { ...route.childRoutes };

		// Overviews, design-notes, and how-to-use are optional content for a product.
		// Check if they've been flagged as absent by the productLine data, and
		// hide them.
		if (productLine && productLine.attributes && productLine.attributes.menus) {
			let idx = -1;

			// Commented out until future implementation requires hiding overviews.
			// Content _is_ technically optional, but we don't handle the situation
			// where this is absent because this is the default landing spot
			// on a /font/fontName URL
			/* if(!productLine.attributes.menus.overview) {
				idx = this.findWithAttr(route.childRoutes, 'name', 'overview');

				if(pageNav[idx] && pageNav[idx].includeInPageHeader) {
					pageNav[idx].includeInPageHeader = false;
				}
			} */
			if (!productLine.attributes.menus.design_notes) {
				idx = this.findWithAttr(route.childRoutes, 'name', 'design-notes');

				if (pageNav[idx] && pageNav[idx].includeInPageHeader) {
					pageNav[idx].includeInPageHeader = false;
				}
			} else {
				idx = this.findWithAttr(route.childRoutes, 'name', 'design-notes');

				if (pageNav[idx] && !pageNav[idx].includeInPageHeader) {
					pageNav[idx].includeInPageHeader = true;
				}
			}
			if (!productLine.attributes.menus.how_to_use) {
				idx = this.findWithAttr(route.childRoutes, 'name', 'how-to-use');

				if (pageNav[idx] && pageNav[idx].includeInPageHeader) {
					pageNav[idx].includeInPageHeader = false;
				}
			} else {
				idx = this.findWithAttr(route.childRoutes, 'name', 'how-to-use');

				if (pageNav[idx] && !pageNav[idx].includeInPageHeader) {
					pageNav[idx].includeInPageHeader = true;
				}
			}
		}

		let title = productLine && productLine.attributes.name;
		if (get(productLine, 'attributes.type_capsule') === 1) {
			const overrideName = get(productLine, 'attributes.override_name');
			title = overrideName || title;
		}

		let labelPrefix = productLine && productLine.attributes.name;
		if (get(productLine, 'attributes.type_capsule') === 1) {
			labelPrefix = 'Capsule';
		}

		return (
			<PageHeader
				breakpoints={ breakpoints }
				changeRoute={ changeRoute }
				childRoute={ childRoute.name }
				containerClassName={ styles.fontPageHeaderContainer }
				isReady={ !!productLine }
				labelPrefix={ labelPrefix }
				location={ location }
				navItems={ pageNav }
				onCollapse={ this.onPageHeaderCollapse }
				params={ params }
				route={ route.name }
				specialOffer={
					hideSpecialOffer
						? null
						: {
							id: get(specialOffers, 'special_offers.special_offers.0.id'),
							license: licenseIsPopulated(licenses) ? message : null,
							name: get(specialOffers, 'special_offers.special_offers.0.name'),
							text: specialOfferText,
							links: specialOfferLinks
						}
				}
				title={ title }
				{ ...childRoute.pageHeader }
				fontId={ productLine && productLine.id }
			/>
		);
	}

	render() {
		const {
			breakpoints,
			children,
			editorControlsOpen,
			location,
			productLine,
			routes
		} = this.props;

		const childRoute = last(routes);

		// Render contents
		if (location.pathname !== '/fonts' && !this.props.params.font) {
			return children;
		} else if (location.pathname !== '/fonts' && this.props.params.font) {
			return (
				<div
					className={ classNames(styles.fonts, editorControlsOpen && breakpoints.gtLargeMedium && styles.fontsControlsOpen) }
				>
					{ this.renderHelmet() }
					{ childRoute.pageHeader && breakpoints.gtLargeMedium && this.renderPageHeader() }
					<div className={ styles.contentContainer }>
						{ productLine && children }
					</div>
					{ this.renderFooter() }
				</div>
			);
		}

		return null;
	}
}

Fonts.propTypes = {
	breakpoints: PropTypes.object.isRequired,
	cart: PropTypes.object,
	changeRoute: PropTypes.func,
	children: PropTypes.node,
	clearSiteHeader: PropTypes.func.isRequired,
	editorControlsOpen: PropTypes.bool,
	isMobile: PropTypes.bool,
	licenses: PropTypes.object,
	location: PropTypes.object,
	onClearProductLine: PropTypes.func.isRequired,
	onLoadProducts: PropTypes.func.isRequired,
	onLoadProductStyles: PropTypes.func.isRequired,
	onLoadSpecialOffers: PropTypes.func.isRequired,
	onLoadSuggestedPairings: PropTypes.func.isRequired,
	onSetProductLine: PropTypes.func.isRequired,
	params: PropTypes.object,
	productLine: PropTypes.object,
	products: PropTypes.array,
	productStyles: PropTypes.array,
	replaceRoute: PropTypes.func.isRequired,
	route: PropTypes.object,
	routes: PropTypes.array,
	setBreadcrumb: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
	onSetFooter: PropTypes.func.isRequired,
	setSiteHeader: PropTypes.func.isRequired,
	specialOffers: PropTypes.object,
	user: PropTypes.object
};

Fonts.defaultProps = {
	cart: null,
	changeRoute: null,
	children: null,
	editorControlsOpen: false,
	isMobile: null,
	licenses: null,
	location: null,
	params: null,
	productLine: null,
	products: null,
	productStyles: null,
	route: null,
	routes: null,
	specialOffers: null,
	user: null
};

const mapStateToProps = createStructuredSelector({
	breakpoints: selectBreakpoints(),
	cart: selectCart(),
	editorControlsOpen: selectEditorControlsOpen(),
	isMobile: selectMobileState(),
	licenses: selectLicenseState(),
	productLine: selectProductLine(),
	products: selectProducts(),
	productStyles: selectProductStyles(),
	specialOffers: selectSpecialOffers(),
	user: selectUserProfile()
});

function mapDispatchToProps(dispatch) {
	return {
		changeRoute: url => dispatch(push(url)),
		clearSiteHeader: () => dispatch(clearSiteHeaderState()),
		onClearProductLine: () => dispatch(clearProductLine()),
		onLoadProducts: () => dispatch(loadProducts()),
		onLoadProductStyles: () => dispatch(loadProductStyles()),
		onLoadSpecialOffers: () => dispatch(loadSpecialOffers()),
		onLoadSuggestedPairings: () => dispatch(loadSuggestedPairings()),
		onSetFooter: data => dispatch(setFooter(data)),
		onSetMenu: (menu) => dispatch(setMenu(menu)),
		onSetProductLine: id => dispatch(setProductLine(id)),
		replaceRoute: url => dispatch(replace(url)),
		setBreadcrumb: data => dispatch(setBreadcrumbState(data)),
		setSiteHeader: data => dispatch(setSiteHeaderState(data)),
		dispatch
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(Fonts);
